import { useState, useRef, useEffect } from "react";
import { HelpCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

interface HelpTooltipProps {
  text: string;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  iconSize?: number;
}

export default function HelpTooltip({ text, side = "bottom", className = "", iconSize = 14 }: HelpTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Close on outside click (delayed to avoid same-click dismiss)
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const timerId = setTimeout(() => {
      if (!mounted) return;
      const handler = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      // Store cleanup
      cleanupRef.current = () => document.removeEventListener("mousedown", handler);
    }, 100);
    return () => {
      mounted = false;
      clearTimeout(timerId);
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [open]);

  const positionClasses: Record<string, string> = {
    top: "bottom-full mb-2 left-1/2 -translate-x-1/2",
    bottom: "top-full mt-2 left-1/2 -translate-x-1/2",
    left: "right-full mr-2 top-1/2 -translate-y-1/2",
    right: "left-full ml-2 top-1/2 -translate-y-1/2",
  };

  const arrowClasses: Record<string, string> = {
    top: "top-full left-1/2 -translate-x-1/2 border-t-popover border-x-transparent border-b-transparent",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-b-popover border-x-transparent border-t-transparent",
    left: "left-full top-1/2 -translate-y-1/2 border-l-popover border-y-transparent border-r-transparent",
    right: "right-full top-1/2 -translate-y-1/2 border-r-popover border-y-transparent border-l-transparent",
  };

  return (
    <div ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(!open);
        }}
        className="text-muted-foreground/50 hover:text-primary transition-colors focus:outline-none"
        aria-label="مساعدة"
      >
        <HelpCircle style={{ width: iconSize, height: iconSize }} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-[9999] w-56 ${positionClasses[side]}`}
          >
            <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-xs text-popover-foreground leading-relaxed">
              <button
                onClick={() => setOpen(false)}
                className="absolute top-1.5 left-1.5 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
              {text}
            </div>
            {/* Arrow */}
            <div className={`absolute w-0 h-0 border-[5px] ${arrowClasses[side]}`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
